// ScientificFromNotationTask.js
// Zapisz liczbę w postaci zwykłej (bez notacji wykładniczej).
// Akceptujemy: zapis dziesiętny (kropka lub przecinek) albo ułamek dziesiętny "N/10^k".
// Zakres: k ∈ [-5, 5], mantysa ~ 1–3 cyfry znaczące. Bez BigInt – wszystko na Number.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// --- helpers (Number) ---
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const int = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

function fmtMantissa(a) {
  // pokaż 1–3 cyfry znaczące bez zbędnych ogonów
  let s = a.toFixed(3);
  s = s.replace(/0+$/,"").replace(/\.$/,"");
  return s;
}

function toRationalFromDecimalString(str) {
  // "0,00123" / "0.00123" / "12,3" -> {num, den} z usuniętym separatorem
  const s = String(str).trim().replace(/\s/g, "").replace(",", ".");
  if (!/^\d*\.?\d+$/.test(s)) return null;
  if (!s.includes(".")) return { num: Number(s), den: 1 };

  const [i, f] = s.split(".");
  const den = 10 ** f.length;
  const num = Number((i || "0") + f);
  return { num, den };
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a;
}

function simplify(num, den) {
  const g = gcd(num, den);
  let n = num / g;
  let d = den / g;
  if (d < 0) { n = -n; d = -d; }
  return { num: n, den: d };
}

function eqRational(a, b) {
  // porównaj przez przekątne (zakres bezpieczny)
  return a.num * b.den === b.num * a.den;
}

function rationalFromPowerOfTenForm(raw) {
  // "123/10000" / "123/10^4" / "123 / (10^4)"
  const s = String(raw).replace(/\s/g,"");
  let m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return simplify(Number(m[1]), Number(m[2]));

  m = s.match(/^(\d+)\/10\^(-?\d+)$/) || s.match(/^(\d+)\/\(10\^(-?\d+)\)$/);
  if (m) {
    const n = Number(m[1]);
    const k = parseInt(m[2],10);
    // U nas den to zawsze dodatnia potęga 10 (forma dziesiętna w mianowniku)
    const den = k>=0 ? 10 ** k : 1;
    return simplify(n, den);
  }
  return null;
}

// --- dokładny zapis dziesiętny dla ułamków z mianownikiem 2^a·5^b ---
function isTerminatingDecimal(den) {
  let d = den;
  while (d % 2 === 0) d /= 2;
  while (d % 5 === 0) d /= 5;
  return d === 1;
}

function toExactDecimalString(num, den) {
  // den = 2^a * 5^b
  let a = 0, b = 0, d = den;
  while (d % 2 === 0) { d /= 2; a++; }
  while (d % 5 === 0) { d /= 5; b++; }
  if (d !== 1) return null;
  const m = Math.max(a, b);
  // skaluj licznik do mianownika 10^m
  const twoPow = (p)=> (p<=0 ? 1 : 2**p);
  const fivePow = (p)=> (p<=0 ? 1 : 5**p);
  const scale = twoPow(m - a) * fivePow(m - b);
  const N = num * scale;          // całkowity
  const s = String(N);
  if (m === 0) return s.replace(".",",");
  if (s.length <= m) {
    const z = "0".repeat(m - s.length);
    return ("0," + z + s).replace(".",",");
  }
  const cut = s.length - m;
  return (s.slice(0, cut) + "," + s.slice(cut)).replace(".",",");
}

// --- generator ---
export function generateTask() {
  const k = int(-5, 5);
  // wymuś ±1..±5; jeśli 0 się trafi, podmień na ±1
  const kEff = (k === 0) ? (Math.random()<0.5 ? -1 : 1) : k;

  // mantysa 1.xy / 9.xy z 1–3 cyframi znaczącymi
  const digits = pick([1,2,3]);
  const base = int(10**(digits-1), 10**digits - 1);   // np. 123
  const a = base / 10**(digits-1);                    // 1.23 .. 9.99
  const mantissaStr = fmtMantissa(a);

  const leftLatex = `${mantissaStr}\\,\\cdot\\,10^{${kEff}}`;
  const kSign = kEff < 0 ? -1 : +1;

  // wartość docelowa jako {num,den} (Number)
  // a = base / 10^(digits-1); wynik = base * 10^(kEff - (digits-1))
  let num = base;
  let den = 1;
  const shift = kEff - (digits-1);
  if (shift >= 0) {
    num = num * (10 ** shift);
  } else {
    den = 10 ** (-shift);
  }
  const target = simplify(num, den);

  return {
    type: "ScientificFromNotationTask",
    data: { leftLatex, k: kEff, kSign, base, digits, target }
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      {/* bardziej podręcznikowe sformułowanie */}
      Oblicz wartość i zapisz wynik w postaci liczby: <InlineMath math={data.leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  const ph = "np.12300 lub 0,00123";
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder={ph}
        label=""
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const raw = String(userAnswer.raw).trim();

  // 1) próba jako liczba dziesiętna (kropka/przecinek)
  const dec = toRationalFromDecimalString(raw);
  if (dec) {
    const simp = simplify(dec.num, dec.den);
    return eqRational(simp, data.target);
  }

  // 2) próba jako ułamek dziesiętny „N/10^k” lub zwykły „N/M”
  const rat = rationalFromPowerOfTenForm(raw);
  if (rat) {
    const simp = simplify(rat.num, rat.den);
    return eqRational(simp, data.target);
  }

  return false;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  // Wyświetlamy to, co wpisał użytkownik (pozwalamy na przecinek lub kropkę).
  const s = String(userAnswer.raw).replace(/\s/g,"").replace(".",",");
  return <InlineMath math={`${s}`} />;
}

export function renderAnswer({ data }) {
  const { num, den } = data.target;

  // Preferuj zapis dziesiętny, jeśli rozwinięcie jest skończone (mianownik = 2^a·5^b)
  if (isTerminatingDecimal(den)) {
    const dec = toExactDecimalString(num, den);
    return <InlineMath math={dec} />;
  }

  // w przeciwnym razie – tylko ułamek zwykły
  const frac = `\\dfrac{${num}}{${den}}`;
  return <InlineMath math={frac} />;
}

export function renderExplanation({ data }) {
  const { k, leftLatex, target } = data;

  const dir = k < 0 ? "w lewo" : "w prawo";
  const steps = Math.abs(k);

  // wynik w zapisie dziesiętnym (preferowany)
  let decLatex = null;
  if (isTerminatingDecimal(target.den)) {
    decLatex = toExactDecimalString(target.num, target.den);
  }

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p><strong>Krok 1:</strong> Mantysa i wykładnik to <InlineMath math={leftLatex} />.</p>
      <p><strong>Krok 2:</strong> Przesuń przecinek o <InlineMath math={`${steps}`} /> miejsce/ miejsca {dir} (bo <InlineMath math={`k=${k}`} />).</p>
      {decLatex
        ? <BlockMath math={`${leftLatex}\\;=\\;${decLatex}`} />
        : <BlockMath math={`\\;\\Rightarrow\\;\\dfrac{${target.num}}{${target.den}}`} />
      }
    </div>
  );
}
